import { useEffect, useState } from "react";
import { api, formatTL } from "@/lib/api";
import { toast } from "sonner";
import { Store, Building2, Plus, KeyRound, Trash2, X, User, Phone, Lock, Power, BarChart3, Package, Download, Printer, Percent, MapPin, Pencil, Search, Copy, Link as LinkIcon } from "lucide-react";

const BranchModal = ({ open, mode, branch, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const isPassword = mode === "password";
  const isEdit = mode === "edit";

  useEffect(() => {
    if (open) {
      setName(isEdit ? branch?.name || "" : "");
      setPhone("");
      setPassword("");
      setCity(isEdit ? branch?.city || "" : "");
      setDistrict(isEdit ? branch?.district || "" : "");
      setAddress(isEdit ? branch?.address || "" : "");
    }
  }, [open, isEdit, branch]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!isPassword && !isEdit && phone.replace(/\D/g, "").length !== 10) {
      toast.error("Telefon numarası 10 haneli olmalıdır");
      return;
    }
    setLoading(true);
    try {
      if (isPassword) {
        await api.put(`/branches/${branch.id}/password`, { password });
        toast.success("Şube şifresi güncellendi");
      } else if (isEdit) {
        await api.put(`/branches/${branch.id}`, { name, city, district, address });
        toast.success("Şube bilgileri güncellendi");
      } else {
        await api.post("/branches", { name, phone, password, city, district, address });
        toast.success("Şube oluşturuldu");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
    setLoading(false);
  };

  const title = isPassword ? `Şifre Ver — ${branch?.name}` : isEdit ? `Şube Düzenle — ${branch?.name}` : "Yeni Şube Ekle";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4" data-testid="branch-modal">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[var(--fb-border)] p-6 animate-fade-up max-h-[90vh] overflow-y-auto fb-scroll">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-extrabold text-xl">{title}</h2>
          <button data-testid="branch-modal-close" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {!isPassword && (
            <>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
                <input
                  data-testid="branch-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Şube adı"
                  className="w-full border border-[var(--fb-border)] rounded-xl pl-9 pr-3 py-3 outline-none focus:border-[var(--fb-red)]"
                />
              </div>
              {!isEdit && (
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
                  <input
                    data-testid="branch-phone"
                    value={phone}
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="Telefon (10 haneli, giriş için)"
                    className="w-full border border-[var(--fb-border)] rounded-xl pl-9 pr-3 py-3 outline-none focus:border-[var(--fb-red)]"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
                  <input
                    data-testid="branch-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="İl"
                    className="w-full border border-[var(--fb-border)] rounded-xl pl-9 pr-3 py-3 outline-none focus:border-[var(--fb-red)]"
                  />
                </div>
                <input
                  data-testid="branch-district"
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  placeholder="İlçe"
                  className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-3 outline-none focus:border-[var(--fb-red)]"
                />
              </div>
              <textarea
                data-testid="branch-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Açık adres"
                rows={2}
                className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-3 outline-none focus:border-[var(--fb-red)] resize-none"
              />
            </>
          )}
          {!isEdit && (
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
              <input
                data-testid="branch-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isPassword ? "Yeni şifre" : "Şifre"}
                className="w-full border border-[var(--fb-border)] rounded-xl pl-9 pr-3 py-3 outline-none focus:border-[var(--fb-red)]"
              />
            </div>
          )}
          <button
            data-testid="branch-modal-submit"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors active:scale-[0.99]"
          >
            {loading ? "Lütfen bekleyin..." : isPassword ? "Şifreyi Kaydet" : isEdit ? "Bilgileri Kaydet" : "Şube Oluştur"}
          </button>
        </form>
      </div>
    </div>
  );
};

const PERIODS = [
  ["all", "Tümü"],
  ["today", "Günlük"],
  ["month", "Aylık"],
  ["range", "Tarih Aralığı"],
];

const periodParams = (period, range = {}) => {
  const now = new Date();
  if (period === "today")
    return {
      start: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(),
      end: now.toISOString(),
    };
  if (period === "month")
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      end: now.toISOString(),
    };
  if (period === "year")
    return {
      start: new Date(now.getFullYear(), 0, 1).toISOString(),
      end: now.toISOString(),
    };
  if (period === "range") {
    const p = {};
    if (range.start) p.start = new Date(range.start + "T00:00:00").toISOString();
    if (range.end) p.end = new Date(range.end + "T23:59:59").toISOString();
    return p;
  }
  return {};
};

const BranchSalesModal = ({ branch, onClose }) => {
  const [period, setPeriod] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!branch) return;
    setLoading(true);
    api
      .get(`/branches/${branch.id}/product-sales`, { params: periodParams(period, range) })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Satışlar yüklenemedi"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, period, range.start, range.end]);

  if (!branch) return null;

  const maxQty = data && data.products.length ? Math.max(1, ...data.products.map((p) => p.quantity)) : 1;
  const periodLabel = () => {
    if (period === "range") return `${range.start || "..."} — ${range.end || "..."}`;
    return PERIODS.find(([v]) => v === period)?.[1] || "";
  };

  const exportRows = () => [
    ["Şube", branch.name],
    ["Dönem", periodLabel()],
    [],
    ["Paket Servis Cirosu", data.delivery_revenue],
    ["Gel-Al/Kiosk Cirosu", data.takeaway_revenue],
    ["Online Link Cirosu", data.link_revenue],
    ["Toplam Ciro", data.total_revenue],
    ["Toplam Sipariş", data.order_count],
    ["Toplam Ürün Adedi", data.total_items],
    [],
    ["Ürün", "Adet"],
    ...data.products.map((p) => [p.name, p.quantity]),
  ];

  const downloadCSV = () => {
    const csv = exportRows()
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${branch.name}-satis-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV indirildi");
  };

  const printReport = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Yazdırma penceresi açılamadı");
    const prodRows = data.products
      .map((p) => `<tr><td>${p.name}</td><td style="text-align:right">${p.quantity}</td></tr>`)
      .join("");
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${branch.name} Satış Raporu</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111}h1{font-size:20px;margin:0 0 4px}p.sub{color:#666;margin:0 0 16px}
      table{width:100%;border-collapse:collapse;margin-top:8px}th,td{border-bottom:1px solid #eee;padding:8px;text-align:left;font-size:13px}
      .rev{display:flex;gap:12px;margin:12px 0}.rev div{flex:1;border:1px solid #eee;border-radius:8px;padding:10px}.rev b{display:block;font-size:16px}
      .k{font-size:11px;text-transform:uppercase;color:#888}</style></head><body>
      <h1>${branch.name} — Satış Raporu</h1><p class="sub">Dönem: ${periodLabel()} · ${data.order_count} sipariş · ${data.total_items} adet ürün</p>
      <div class="rev">
        <div><span class="k">Paket Servis</span><b>${formatTL(data.delivery_revenue)}</b></div>
        <div><span class="k">Gel-Al / Kiosk</span><b>${formatTL(data.takeaway_revenue)}</b></div>
        <div><span class="k">Toplam Ciro</span><b>${formatTL(data.total_revenue)}</b></div>
      </div>
      <table><thead><tr><th>Ürün</th><th style="text-align:right">Adet</th></tr></thead><tbody>${prodRows}</tbody></table>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4" data-testid="branch-sales-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-[var(--fb-border)] p-6 animate-fade-up max-h-[85vh] overflow-y-auto fb-scroll">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-extrabold text-xl flex items-center gap-2">
            <Package className="w-5 h-5 text-[var(--fb-red)]" /> {branch.name} — Ürün Satışları
          </h2>
          <button data-testid="branch-sales-close" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-1 bg-[var(--fb-neutral)] rounded-full p-1 border border-[var(--fb-border)] w-fit mb-4">
          {PERIODS.map(([v, l]) => (
            <button
              key={v}
              data-testid={`branch-sales-period-${v}`}
              onClick={() => setPeriod(v)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                period === v ? "bg-[var(--fb-red)] text-white" : "text-[var(--fb-muted)]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {period === "range" && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <input
              type="date"
              data-testid="branch-sales-start"
              value={range.start}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="bg-white border border-[var(--fb-border)] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[var(--fb-red)]"
            />
            <span className="text-[var(--fb-muted)]">—</span>
            <input
              type="date"
              data-testid="branch-sales-end"
              value={range.end}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="bg-white border border-[var(--fb-border)] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[var(--fb-red)]"
            />
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-[var(--fb-muted)]">Yükleniyor...</div>
        ) : !data ? (
          <div className="py-10 text-center text-[var(--fb-muted)]">Yükleniyor...</div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <button
                data-testid="branch-sales-csv"
                onClick={downloadCSV}
                className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-red)] border border-[var(--fb-border)] hover:border-[var(--fb-red)] px-4 py-2 rounded-full transition-colors"
              >
                <Download className="w-4 h-4" /> CSV İndir
              </button>
              <button
                data-testid="branch-sales-print"
                onClick={printReport}
                className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-ink)] border border-[var(--fb-border)] hover:border-[var(--fb-ink)] px-4 py-2 rounded-full transition-colors"
              >
                <Printer className="w-4 h-4" /> Yazdır
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div data-testid="branch-rev-delivery" className="rounded-xl border border-[var(--fb-red)] bg-red-50 p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Paket Servis</p>
                <p className="font-display font-extrabold text-lg mt-0.5 text-[var(--fb-red)]">{formatTL(data.delivery_revenue)}</p>
              </div>
              <div data-testid="branch-rev-takeaway" className="rounded-xl border border-[var(--fb-border)] bg-white p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Gel-Al / Kiosk</p>
                <p className="font-display font-extrabold text-lg mt-0.5">{formatTL(data.takeaway_revenue)}</p>
              </div>
              <div data-testid="branch-rev-link" className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Online Link</p>
                <p className="font-display font-extrabold text-lg mt-0.5 text-blue-600">{formatTL(data.link_revenue || 0)}</p>
              </div>
              <div data-testid="branch-rev-total" className="rounded-xl border border-[var(--fb-ink)] bg-[var(--fb-ink)] text-white p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-white/70">Toplam Ciro</p>
                <p className="font-display font-extrabold text-lg mt-0.5">{formatTL(data.total_revenue)}</p>
              </div>
            </div>

            {data.products.length === 0 ? (
              <div data-testid="branch-sales-empty" className="py-8 text-center text-[var(--fb-muted)]">
                Bu dönemde satış yok.
              </div>
            ) : (
              <>
                <p className="text-sm text-[var(--fb-muted)] mb-3">
                  Toplam <span className="font-bold text-[var(--fb-ink)]">{data.total_items} adet</span> ürün ·{" "}
                  {data.order_count} sipariş
                </p>
                <div className="space-y-3">
                  {data.products.map((p) => (
                    <div key={p.name} data-testid={`branch-sales-product-${p.name}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold truncate mr-2">{p.name}</span>
                        <span className="text-[var(--fb-ink)] font-bold whitespace-nowrap">{p.quantity} adet</span>
                      </div>
                      <div className="h-2.5 bg-[var(--fb-neutral)] rounded-full overflow-hidden">
                        <div className="h-full bg-[var(--fb-red)] rounded-full transition-all" style={{ width: `${(p.quantity / maxQty) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const BranchesPanel = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState({ open: false, mode: "create", branch: null });
  const [salesBranch, setSalesBranch] = useState(null);
  const [period, setPeriod] = useState("month");
  const [query, setQuery] = useState("");
  const [rate, setRate] = useState(2);
  const [rateInput, setRateInput] = useState("2");
  const [onlineRate, setOnlineRate] = useState(10);
  const [onlineRateInput, setOnlineRateInput] = useState("10");

  const load = () => {
    setLoading(true);
    setError(false);
    api
      .get("/branches", { params: periodParams(period), timeout: 20000 })
      .then((r) => setBranches(r.data))
      .catch(() => {
        setError(true);
        toast.error("Şubeler yüklenemedi");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    api
      .get("/settings/commission")
      .then((r) => {
        setRate(r.data.rate);
        setRateInput(String(r.data.rate));
      })
      .catch(() => {});
    api
      .get("/settings/online-commission")
      .then((r) => {
        setOnlineRate(r.data.rate);
        setOnlineRateInput(String(r.data.rate));
      })
      .catch(() => {});
  }, []);

  const saveRate = async () => {
    const v = parseFloat(rateInput);
    if (isNaN(v) || v < 0 || v > 100) return toast.error("Geçerli bir oran girin (0-100)");
    try {
      const { data } = await api.put("/settings/commission", { rate: v });
      setRate(data.rate);
      setRateInput(String(data.rate));
      toast.success("Pay oranı kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  const saveOnlineRate = async () => {
    const v = parseFloat(onlineRateInput);
    if (isNaN(v) || v < 0 || v > 100) return toast.error("Geçerli bir oran girin (0-100)");
    try {
      const { data } = await api.put("/settings/online-commission", { rate: v });
      setOnlineRate(data.rate);
      setOnlineRateInput(String(data.rate));
      toast.success("Online komisyon oranı kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  const copyOrderLink = (code) => {
    navigator.clipboard?.writeText(`${window.location.origin}/?sube=${code}`);
    toast.success("Sipariş linki kopyalandı");
  };

  const removeBranch = async (b) => {
    if (!window.confirm(`"${b.name}" şubesini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Şube silindi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silme başarısız");
    }
  };

  const toggleStatus = async (b) => {
    const next = !(b.is_active !== false);
    try {
      await api.put(`/branches/${b.id}/status`, { active: next });
      toast.success(next ? "Şube aktif edildi" : "Şube kapatıldı");
      setBranches((prev) => prev.map((x) => (x.id === b.id ? { ...x, is_active: next } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Durum güncellenemedi");
    }
  };

  const summary = [
    { label: "Toplam Şube", value: branches.length, icon: Building2, color: "text-blue-600" },
  ];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? branches.filter((b) =>
        [b.name, b.phone, b.branch_code, b.city, b.district, b.address]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    : branches;
  const totalRevenue = filtered.reduce((s, b) => s + (b.revenue || 0), 0);
  const totalLink = filtered.reduce((s, b) => s + (b.link_revenue || 0), 0);
  const totalOnlineCommission = totalLink * (onlineRate / 100);
  const maxLinkRev = Math.max(1, ...filtered.map((b) => b.link_revenue || 0));
  const maxRev = Math.max(1, ...filtered.map((b) => b.revenue || 0));

  return (
    <div data-testid="branches-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {summary.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">{c.label}</p>
                <p className="font-display font-extrabold text-2xl mt-1">{c.value}</p>
              </div>
              <Icon className={`w-9 h-9 ${c.color}`} />
            </div>
          );
        })}
        <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Pay Oranı (%)</p>
            <div className="flex items-center gap-2 mt-1">
              <input
                data-testid="commission-input"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                className="w-20 border border-[var(--fb-border)] rounded-xl px-2 py-1.5 outline-none focus:border-[var(--fb-red)] font-bold"
              />
              <button
                data-testid="commission-save"
                onClick={saveRate}
                className="bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white text-sm font-bold px-4 py-1.5 rounded-full transition-colors active:scale-95"
              >
                Kaydet
              </button>
            </div>
          </div>
          <Percent className="w-9 h-9 text-green-600" />
        </div>
        <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Online Komisyon (%)</p>
            <div className="flex items-center gap-2 mt-1">
              <input
                data-testid="online-commission-input"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={onlineRateInput}
                onChange={(e) => setOnlineRateInput(e.target.value)}
                className="w-20 border border-[var(--fb-border)] rounded-xl px-2 py-1.5 outline-none focus:border-[var(--fb-red)] font-bold"
              />
              <button
                data-testid="online-commission-save"
                onClick={saveOnlineRate}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-1.5 rounded-full transition-colors active:scale-95"
              >
                Kaydet
              </button>
            </div>
            <p className="text-[10px] text-[var(--fb-muted)] mt-1">Online (Link) siparişlerine uygulanır</p>
          </div>
          <Percent className="w-9 h-9 text-blue-600" />
        </div>
      </div>

      {filtered.length > 0 && (
        <div data-testid="branch-link-comparison" className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 mb-6">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <span className="font-display font-bold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[var(--fb-red)]" /> Şube Ciro Karşılaştırması
            </span>
            <div className="flex items-center gap-4 text-xs text-[var(--fb-muted)]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[var(--fb-red)]" /> Toplam <span className="font-bold text-[var(--fb-ink)]">{formatTL(totalRevenue)}</span></span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-600" /> Online <span className="font-bold text-blue-600">{formatTL(totalLink)}</span></span>
            </div>
          </div>
          {totalRevenue === 0 && totalLink === 0 ? (
            <p className="text-sm text-[var(--fb-muted)] text-center py-6">Bu dönemde ciro yok.</p>
          ) : (
            <div className="space-y-4">
              {[...filtered]
                .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
                .map((b) => {
                  const rev = b.revenue || 0;
                  const link = b.link_revenue || 0;
                  const revPct = rev > 0 ? Math.max(2, Math.round((rev / maxRev) * 100)) : 0;
                  const linkPct = link > 0 ? Math.max(2, Math.round((link / maxLinkRev) * 100)) : 0;
                  return (
                    <div key={b.id} data-testid={`link-cmp-${b.branch_code}`}>
                      <p className="font-semibold text-sm truncate mb-1.5">{b.name}</p>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2.5 rounded-full bg-red-50 overflow-hidden">
                            <div className="h-full rounded-full bg-[var(--fb-red)] transition-all" style={{ width: `${revPct}%` }} />
                          </div>
                          <span className="text-xs font-bold text-[var(--fb-ink)] whitespace-nowrap w-24 text-right">{formatTL(rev)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2.5 rounded-full bg-blue-50 overflow-hidden">
                            <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${linkPct}%` }} />
                          </div>
                          <span className="text-xs font-bold text-blue-600 whitespace-nowrap w-24 text-right">{formatTL(link)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[var(--fb-border)] overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--fb-border)] flex items-center justify-between gap-2">
          <span className="font-display font-bold flex items-center gap-2">
            <Store className="w-5 h-5 text-[var(--fb-red)]" /> Tüm Şubeler
          </span>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-[var(--fb-neutral)] rounded-full p-1 border border-[var(--fb-border)]">
              {[
                ["today", "Gün"],
                ["month", "Ay"],
                ["year", "Yıl"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`branches-period-${v}`}
                  onClick={() => setPeriod(v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    period === v ? "bg-[var(--fb-red)] text-white" : "text-[var(--fb-muted)]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <button
              data-testid="add-branch-btn"
              onClick={() => setModal({ open: true, mode: "create", branch: null })}
              className="flex items-center gap-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold px-4 py-2 rounded-full transition-colors active:scale-95 text-sm"
            >
              <Plus className="w-4 h-4" /> Yeni Şube Ekle
            </button>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-[var(--fb-border)]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
            <input
              data-testid="branch-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Şube ara (ad, il, ilçe, adres, telefon, kod)"
              className="w-full bg-white border border-[var(--fb-border)] rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[var(--fb-red)]"
            />
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[var(--fb-muted)]">Yükleniyor...</div>
        ) : error ? (
          <div data-testid="branches-error" className="p-10 text-center">
            <p className="text-[var(--fb-muted)] mb-3">Şubeler yüklenemedi. Bağlantınızı kontrol edip tekrar deneyin.</p>
            <button
              data-testid="branches-retry-btn"
              onClick={load}
              className="inline-flex items-center gap-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold px-5 py-2.5 rounded-full transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="branches-empty" className="p-10 text-center text-[var(--fb-muted)]">
            {branches.length === 0
              ? 'Henüz şube yok. "Yeni Şube Ekle" ile ilk şubeyi oluşturun.'
              : "Aramanıza uygun şube bulunamadı."}
          </div>
        ) : (
          <div className="divide-y divide-[var(--fb-border)]">
            {filtered.map((b) => (
              <div key={b.id} data-testid={`branch-row-${b.branch_code}`} className="p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[160px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{b.name}</p>
                    <span
                      data-testid={`branch-status-badge-${b.branch_code}`}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        b.is_active !== false ? "bg-green-50 text-green-600" : "bg-red-50 text-[var(--fb-red)]"
                      }`}
                    >
                      {b.is_active !== false ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--fb-muted)]">{b.phone}</p>
                  {(b.city || b.district || b.address) && (
                    <p data-testid={`branch-address-${b.branch_code}`} className="text-xs text-[var(--fb-muted)] mt-0.5 flex items-start gap-1">
                      <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{[b.city, b.district].filter(Boolean).join(" / ")}{b.address ? ` — ${b.address}` : ""}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid={`branch-sales-${b.branch_code}`}
                    onClick={() => setSalesBranch(b)}
                    className="flex items-center gap-1 text-xs font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-red)] border border-[var(--fb-border)] hover:border-[var(--fb-red)] px-3 py-1 rounded-full transition-colors"
                  >
                    <BarChart3 className="w-3.5 h-3.5" /> Satışlar
                  </button>
                  <button
                    data-testid={`branch-order-link-${b.branch_code}`}
                    onClick={() => copyOrderLink(b.branch_code)}
                    className="flex items-center gap-1 text-xs font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-red)] border border-[var(--fb-border)] hover:border-[var(--fb-red)] px-3 py-1 rounded-full transition-colors"
                  >
                    <LinkIcon className="w-3.5 h-3.5" /> Sipariş Linki
                  </button>
                  <button
                    data-testid={`branch-status-${b.branch_code}`}
                    onClick={() => toggleStatus(b)}
                    className={`flex items-center gap-1 text-xs font-semibold border px-3 py-1 rounded-full transition-colors ${
                      b.is_active !== false
                        ? "text-green-600 border-green-200 hover:border-green-500"
                        : "text-[var(--fb-red)] border-red-200 hover:border-[var(--fb-red)]"
                    }`}
                  >
                    <Power className="w-3.5 h-3.5" /> {b.is_active !== false ? "Kapat" : "Aç"}
                  </button>
                  <button
                    data-testid={`branch-edit-${b.branch_code}`}
                    onClick={() => setModal({ open: true, mode: "edit", branch: b })}
                    className="flex items-center gap-1 text-xs font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-ink)] border border-[var(--fb-border)] hover:border-[var(--fb-ink)] px-3 py-1 rounded-full transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Düzenle
                  </button>
                  <button
                    data-testid={`branch-password-${b.branch_code}`}
                    onClick={() => setModal({ open: true, mode: "password", branch: b })}
                    className="flex items-center gap-1 text-xs font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-ink)] border border-[var(--fb-border)] hover:border-[var(--fb-ink)] px-3 py-1 rounded-full transition-colors"
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Şifre Ver
                  </button>
                  <button
                    data-testid={`branch-delete-${b.branch_code}`}
                    onClick={() => removeBranch(b)}
                    className="flex items-center gap-1 text-xs font-semibold text-[var(--fb-red)] border border-[var(--fb-border)] hover:border-[var(--fb-red)] px-3 py-1 rounded-full transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Sil
                  </button>
                </div>
                <div className="text-right min-w-[100px]">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Ciro</p>
                  <p data-testid={`branch-revenue-${b.branch_code}`} className="font-display font-bold text-[var(--fb-red)]">{formatTL(b.revenue)}</p>
                </div>
                <div className="text-right min-w-[110px]">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Online (Link)</p>
                  <p data-testid={`branch-link-revenue-${b.branch_code}`} className="font-display font-bold text-blue-600">{formatTL(b.link_revenue || 0)}</p>
                </div>
                <div className="text-right min-w-[120px]">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Online Kom. (%{onlineRate})</p>
                  <p data-testid={`branch-online-commission-${b.branch_code}`} className="font-display font-bold text-orange-600">{formatTL((b.link_revenue || 0) * (onlineRate / 100))}</p>
                </div>
                <div className="text-right min-w-[100px]">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Pay (%{rate})</p>
                  <p data-testid={`branch-commission-${b.branch_code}`} className="font-display font-bold text-green-600">{formatTL((b.revenue || 0) * (rate / 100))}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div data-testid="branches-totals" className="flex items-center justify-end gap-8 px-4 py-4 border-t-2 border-[var(--fb-ink)] bg-[var(--fb-neutral)]">
            <span className="font-display font-extrabold text-sm uppercase tracking-wider text-[var(--fb-muted)]">Toplam</span>
            <div className="text-right min-w-[100px]">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Ciro</p>
              <p data-testid="branches-total-revenue" className="font-display font-extrabold text-lg text-[var(--fb-red)]">{formatTL(totalRevenue)}</p>
            </div>
            <div className="text-right min-w-[110px]">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Online (Link)</p>
              <p data-testid="branches-total-link" className="font-display font-extrabold text-lg text-blue-600">{formatTL(totalLink)}</p>
            </div>
            <div className="text-right min-w-[120px]">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Online Kom. (%{onlineRate})</p>
              <p data-testid="branches-total-online-commission" className="font-display font-extrabold text-lg text-orange-600">{formatTL(totalOnlineCommission)}</p>
            </div>
            <div className="text-right min-w-[100px]">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Pay (%{rate})</p>
              <p data-testid="branches-total-commission" className="font-display font-extrabold text-lg text-green-600">{formatTL(totalRevenue * (rate / 100))}</p>
            </div>
          </div>
        )}
      </div>

      <BranchModal
        open={modal.open}
        mode={modal.mode}
        branch={modal.branch}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onSaved={load}
      />

      <BranchSalesModal branch={salesBranch} onClose={() => setSalesBranch(null)} />
    </div>
  );
};
